import { TellescopeModelFromName, useAuth, useTellescopeMutation } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { pendo } from "../../../../../tracking";
import {
  MessageCompositionFooter,
  MessageCompositionFooterPayload,
} from "../../../../../components/MessageCompositionFooter";

export interface InboxChatCompositionFooterProps {
  enduserId?: string;
  subject?: string;
  onSend?: (chatRoom: TellescopeModelFromName<"chat_rooms">, message: TellescopeModelFromName<"chats">) => void;
}

export const InboxChatCompositionFooter = ({ enduserId, subject, onSend }: InboxChatCompositionFooterProps) => {
  const { t } = useTranslation("inbox");
  const { currentUser } = useAuth();

  const createRoomMutation = useTellescopeMutation({
    model: "chat_rooms",
    method: "createOne",
    onError: (error) => console.error("Failed to create chat room:", error),
  });

  const sendMessageMutation = useTellescopeMutation({
    model: "chats",
    method: "createOne",
    onError: (error) => console.error("Failed to send chat message:", error),
  });

  const handleSend = ({ message, attachments }: MessageCompositionFooterPayload, sendAt?: Date) => {
    if (!enduserId || !subject || !currentUser.id) return;

    createRoomMutation.mutate(
      { title: subject, enduserIds: [enduserId] },
      {
        onSuccess: (createdRoom) => {
          sendMessageMutation.mutate(
            {
              roomId: createdRoom.id,
              message,
              userId: currentUser.id,
              attachments: attachments.map(({ type, name, secureName }) => ({ type, name: name ?? "", secureName })),
              linkOpenTrackingIds: [],
              ...(sendAt && { sendAt }),
            },
            { onSuccess: (sentMessage) => onSend?.(createdRoom, sentMessage) }
          );
        },
      }
    );
  };

  const canSend =
    !!enduserId && !!subject && !!currentUser.id && !sendMessageMutation.isPending && !createRoomMutation.isPending;

  return (
    <MessageCompositionFooter
      placeholder={t("Type your message...")}
      canSend={canSend}
      enduserId={enduserId}
      channelType="chat"
      onSubmit={(payload) => {
        pendo.track("new-chat-thread-send");
        handleSend(payload);
      }}
      onSchedule={(payload, sendAt) => handleSend(payload, sendAt)}
      trackingIds={{ schedule: "chat-compose-schedule-message-dialog-open" }}
    />
  );
};
